import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, map, of, startWith, switchMap } from 'rxjs';
import { StagesService } from '@cosmos/angular/api';
import { StageTreeNode } from '@cosmos/shared';

interface TreeState {
  loading: boolean;
  error: boolean;
  roots: StageTreeNode[];
}

const LOADING: TreeState = { loading: true, error: false, roots: [] };
const ERROR: TreeState = { loading: false, error: true, roots: [] };
const EMPTY: TreeState = { loading: false, error: false, roots: [] };

/**
 * Project-scoped stage-forest store for the monitoring module. Single
 * source of truth for the monitoring tree rail (MonitoringTreeRailComponent)
 * — replaces the per-view fetch pipeline MeasurementsQuickSwitchComponent
 * used to own. Callers that mutate the tree's shape (create/move a stage,
 * create a measurement, change a measurement's stage) call refresh()
 * alongside their own local view refresh — this store does not replace
 * a page's own data signals, it only feeds the rail.
 *
 * Same two-call pattern as the quick-switch and StagePage.fetchProjectStageTree
 * used before it: root stages first, then their full descendant trees in one
 * batched request. Projects only display fields — no time/timeAll arrays.
 */
@Injectable({ providedIn: 'root' })
export class MonitoringTreeService {
  private readonly stagesService = inject(StagesService);

  private readonly projectId = signal<string | null>(null);
  private readonly refreshTrigger = signal(0);

  private readonly state: Signal<TreeState> = toSignal(
    toObservable(computed(() => ({ projectId: this.projectId(), tick: this.refreshTrigger() }))).pipe(
      switchMap(({ projectId }) => {
        if (!projectId) return of(EMPTY);
        return this.stagesService.find(`?project=${projectId}&rootOnly=true`).pipe(
          switchMap((rootsRes) => {
            const roots = (rootsRes.body as any[]) ?? [];
            if (roots.length === 0) return of(EMPTY);
            return this.stagesService.getForest(roots.map((r) => r._id)).pipe(
              map((res): TreeState => ({ loading: false, error: false, roots: (res.body ?? []) as StageTreeNode[] })),
            );
          }),
          catchError(() => of(ERROR)),
          startWith(LOADING),
        );
      }),
    ),
    { initialValue: LOADING },
  );

  readonly roots: Signal<StageTreeNode[]> = computed(() => this.state().roots);
  readonly loading: Signal<boolean> = computed(() => this.state().loading);
  readonly error: Signal<boolean> = computed(() => this.state().error);

  private currentProjectId(): string | null {
    return this.projectId();
  }

  setProject(projectId: string | null): void {
    if (projectId === this.currentProjectId()) return;
    this.projectId.set(projectId);
  }

  refresh(): void {
    this.refreshTrigger.update((n) => n + 1);
  }

  refreshFor(projectId: string): void {
    if (projectId === this.currentProjectId()) this.refresh();
  }

  /** Walks the in-memory forest to find a stage's ancestor chain
   *  (root-first, self-last). Returns [] if the stage isn't in the
   *  currently-loaded tree (still loading, wrong project, or stale). */
  ancestorChain(stageId: string): StageTreeNode[] {
    for (const root of this.roots()) {
      const chain = this.findChain(root, stageId, []);
      if (chain) return chain;
    }
    return [];
  }

  private findChain(node: StageTreeNode, targetId: string, pathSoFar: StageTreeNode[]): StageTreeNode[] | null {
    const path = [...pathSoFar, node];
    if (node._id === targetId) return path;
    for (const child of node.children) {
      const found = this.findChain(child, targetId, path);
      if (found) return found;
    }
    return null;
  }

  /** Walks the in-memory forest to find which stage owns a given
   *  measurement id. Returns null if not found in the currently-loaded tree. */
  findMeasurementStageId(measurementId: string): string | null {
    for (const root of this.roots()) {
      const found = this.findMeasurementOwner(root, measurementId);
      if (found) return found;
    }
    return null;
  }

  private findMeasurementOwner(node: StageTreeNode, measurementId: string): string | null {
    if (node.measurements.some((m) => m._id === measurementId)) return node._id;
    for (const child of node.children) {
      const found = this.findMeasurementOwner(child, measurementId);
      if (found) return found;
    }
    return null;
  }
}
